import * as React from 'react';
import { message, Select } from 'antd';
import {
  searchHubWithrealFilter,
  getVendorList,
} from 'src/components/pages/AssignedDrivers/AssignedDrivers.api';

const { Option } = Select;

/**
 * Common hook for managing vendor data
 */
export const useVendorFetcher = (form: any, t: any, fieldName: string = 'vendor_code') => {
  const [vendorNames, setVendorNames] = React.useState<any[]>([]);
  const [vendorLoading, setVendorLoading] = React.useState<boolean>(true);

  const fetchVendorList = async () => {
    setVendorLoading(true);
    const response = await getVendorList();
    if (!response.isSuccess) {
      message.error(response?.errorMessage || t('Error fetching vendor list'));
      setVendorLoading(false);
      return;
    }
    setVendorNames(response?.data || []);
    const vendorDetails = response?.data.find(
      (vendor: any) => vendor.id === localStorage.getItem('userId'),
    );
    if (vendorDetails) {
      form.setFieldsValue({ [fieldName]: vendorDetails.code });
    }
    setVendorLoading(false);
  };

  return { vendorNames, vendorLoading, fetchVendorList };
};

/**
 * Common hook for managing hub search with debounce
 */
export const useHubFetcher = (debouncedHubQuery: string, t: any) => {
  const [hubOptions, setHubOptions] = React.useState<any[]>([]);

  React.useEffect(() => {
    const fetchHubOptions = async () => {
      if (!debouncedHubQuery || !debouncedHubQuery.trim()) {
        setHubOptions([]);
        return;
      }

      if (debouncedHubQuery.trim().length >= 1) {
        const body = { searchQuery: debouncedHubQuery, realFilter: true };
        const response = await searchHubWithrealFilter(body);
        if (!response?.isSuccess) {
          message.error(response.errorMessage || t('Error fetching hub options'));
        }
        setHubOptions(response?.data || []);
      }
    };
    fetchHubOptions();
  }, [debouncedHubQuery]);

  return { hubOptions, setHubOptions };
};

/**
 * Common utility to check if field is disabled
 */
export const isFieldDisabled = (type: string, action?: string): boolean => {
  return type === 'disabled' || (type === 'view_only' && action === 'edit');
};

/**
 * Common hub select renderer
 */
export const renderHubCodeSelect = (
  hubOptions: any[],
  handleHubSearch: (value: string) => void,
  disabled: boolean,
  t: any,
  labelInValue: boolean = true,
) => (
  <Select
    showSearch
    allowClear
    placeholder={t('Select Hub')}
    optionFilterProp="children"
    defaultActiveFirstOption={false}
    showArrow
    onSearch={handleHubSearch}
    disabled={disabled}
    labelInValue={labelInValue}
  >
    {hubOptions.map((hub: any) => (
      <Option key={hub.code} value={hub.code}>
        {`${hub.name}, ${hub.code}`}
      </Option>
    ))}
  </Select>
);

// Generate default labels map from fields list
export const generateDefaultLabels = (fields_list: any[]) => {
  return (fields_list || []).reduce((acc: any, item: any) => {
    acc[item.name] = item.label;
    return acc;
  }, {});
};

// Generate field list map with key as identifier
export const generateFieldListMap = (fields_list: any[]) => {
  return (fields_list || []).reduce((acc: any, item: any) => {
    const { key, ...rest } = item;
    acc[key] = { key, ...rest };
    return acc;
  }, {});
};
